import { forwardRef, useRef } from 'react'

import { clsx } from 'clsx'
import { createPortal } from 'react-dom'

import { DesktopDropdown } from './DesktopDropdown'
import { MobileBottomSheet } from './MobileBottomSheet'
import {
  type SelectionHandlers,
  type TimeState,
  useIsTimeInRange,
  useSelectionHandlers,
  useTimeOutsideClick,
  useTimePickerState,
} from './TimePicker-helpers'
import { type TimePickerProps, sizeStyles, validationStyles } from './types'
import { useIsMobile } from '../popover'

interface InputBlockProps {
  inputRef: React.ForwardedRef<HTMLInputElement>
  state: TimeState
  handlers: SelectionHandlers
  validationState: 'default' | 'invalid' | 'valid'
  size: 'sm' | 'md' | 'lg'
  className?: string
  id?: string
  placeholder: string
  isDisabled?: boolean
  showSeconds: boolean
  value: TimePickerProps['value']
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
}

function TimeInputBlock(p: InputBlockProps): JSX.Element {
  return (
    <div className="relative">
      <input
        ref={p.inputRef}
        aria-controls={p.state.isOpen ? `${p.id ?? 'time-picker'}-dropdown` : undefined}
        aria-expanded={p.state.isOpen}
        aria-haspopup="dialog"
        aria-invalid={p.validationState === 'invalid'}
        className={clsx(
          'w-full rounded-md border',
          'bg-card text-foreground',
          'placeholder:text-muted-foreground',
          'focus:ring-1 focus:outline-none',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors duration-150 pr-10',
          sizeStyles[p.size],
          validationStyles[p.validationState],
          p.className,
        )}
        disabled={p.isDisabled}
        id={p.id}
        placeholder={p.placeholder}
        role="combobox"
        type="text"
        value={p.state.inputValue}
        onBlur={() => p.handlers.handleInputBlur(p.value, p.showSeconds)}
        onChange={p.handlers.handleInputChange}
        onFocus={() => p.state.setIsOpen(true)}
        {...p.inputProps}
      />
      <ClockIconButton disabled={p.isDisabled} onClick={() => !p.isDisabled && p.state.setIsOpen(!p.state.isOpen)} />
    </div>
  )
}

interface MessageProps {
  hideMessage: boolean
  showError: boolean
  error?: string
  helperText?: string
}

function TimeFieldMessage({ hideMessage, showError, error, helperText }: MessageProps): JSX.Element | null {
  if (hideMessage) { return null }
  return (
    <div className="field-message">
      {showError && <p className="text-sm text-red-500" role="alert">{error}</p>}
      {!showError && helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}

interface UseTimePickerHookResult {
  state: TimeState
  handlers: SelectionHandlers
  isMobile: boolean
  containerRef: React.RefObject<HTMLDivElement>
}

interface UseTimePickerHookInput {
  value: TimePickerProps['value']
  format: '12' | '24'
  showSeconds: boolean
  minuteStep: number
  onChange?: TimePickerProps['onChange']
  minTime?: TimePickerProps['value']
  maxTime?: TimePickerProps['value']
}

function useTimePickerHook(input: UseTimePickerHookInput): UseTimePickerHookResult {
  const { value, format, showSeconds: shouldShowSeconds, minuteStep, onChange, minTime, maxTime } = input
  const containerRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const state = useTimePickerState({ value, format, showSeconds: shouldShowSeconds })
  const isTimeInRange = useIsTimeInRange(minTime ?? undefined, maxTime ?? undefined)
  const handlers = useSelectionHandlers({ state, format, isTimeInRange, onChange, minuteStep })
  useTimeOutsideClick(containerRef, state.isOpen, isMobile, state.setIsOpen)
  return { state, handlers, isMobile, containerRef }
}

export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (props, ref) => {
    const {
      value, onChange, format = '24', showSeconds: shouldShowSeconds = false, minuteStep = 5,
      placeholder, minTime, maxTime, validationState: validationStateProp, error, helperText,
      size = 'md', hideMessage: shouldHideMessage = false, disabled: isDisabled, id, className,
      ...inputProps
    } = props

    const { state, handlers, isMobile, containerRef } = useTimePickerHook({
      value, format, showSeconds: shouldShowSeconds, minuteStep, onChange, minTime, maxTime,
    })

    const validationState = error ? 'invalid' : (validationStateProp ?? 'default')
    const hasError = !!error && validationState === 'invalid'
    const defaultPlaceholder = format === '12' ? 'HH:MM AM/PM' : 'HH:MM'

    const timePickerProps = {
      format, minuteStep,
      selectedHour: state.selectedHour,
      selectedMinute: state.selectedMinute,
      selectedPeriod: state.selectedPeriod,
      onHourSelect: handlers.handleHourSelect,
      onMinuteSelect: handlers.handleMinuteSelect,
      onPeriodSelect: handlers.handlePeriodSelect,
      onNowClick: handlers.handleNowClick,
    }

    return (
      <div ref={containerRef} className="relative w-full">
        <TimeInputBlock
          className={className}
          handlers={handlers}
          id={id}
          inputProps={inputProps}
          inputRef={ref}
          isDisabled={isDisabled}
          placeholder={placeholder ?? defaultPlaceholder}
          showSeconds={shouldShowSeconds}
          size={size}
          state={state}
          validationState={validationState}
          value={value}
        />
        {state.isOpen && !isMobile && <DesktopDropdown {...timePickerProps} />}
        {state.isOpen && isMobile && createPortal(
          <MobileBottomSheet {...timePickerProps} onClose={() => state.setIsOpen(false)} />,
          document.body,
        )}
        <TimeFieldMessage error={error} helperText={helperText} hideMessage={shouldHideMessage} showError={hasError} />
      </div>
    )
  },
)

TimePicker.displayName = 'TimePicker'

interface ClockIconButtonProps {
  disabled?: boolean
  onClick: () => void
}

function ClockIconButton({ disabled, onClick }: ClockIconButtonProps): JSX.Element {
  return (
    <button
      aria-label="Open time picker"
      className={clsx(
        'absolute inset-y-0 right-0 flex items-center pr-3',
        'text-muted-foreground hover:text-foreground',
        'disabled:opacity-50 disabled:cursor-not-allowed',
      )}
      disabled={disabled}
      tabIndex={-1}
      type="button"
      onClick={onClick}
    >
      <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    </button>
  )
}

export default TimePicker
